import { useParams } from 'react-router-dom';
import {
  makeStyles, tokens, Title2, Body1, Spinner,
  MessageBar, MessageBarBody, Tree, TreeItem, TreeItemLayout,
} from '@fluentui/react-components';
import { useEpicTree } from '../../api/hooks';
import { StatusPill } from '../../components/common/StatusPill';
import type { EpicTreeNode } from '../../api/types';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  meta: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  itemLabel: { display: 'inline-flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  type: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
});

function renderNode(node: EpicTreeNode, styles: ReturnType<typeof useStyles>) {
  const hasChildren = node.children?.length > 0;
  const sp = node.storyPoints ? ` · ${node.storyPoints} SP` : '';
  return (
    <TreeItem itemType={hasChildren ? 'branch' : 'leaf'} value={`${node.id}`} key={node.id}>
      <TreeItemLayout>
        <span className={styles.itemLabel}>
          <span className={styles.type}>[{node.workItemType}]</span>
          <span>{node.title}</span>
          <StatusPill state={node.state} size="small" />
          <Body1>{sp}</Body1>
        </span>
      </TreeItemLayout>
      {hasChildren ? (
        <Tree aria-label={node.title}>
          {node.children.map((c) => renderNode(c, styles))}
        </Tree>
      ) : null}
    </TreeItem>
  );
}

export function EpicTreePage() {
  const styles = useStyles();
  const { projectId, epicId } = useParams<{ projectId: string; epicId: string }>();
  const epicIdNum = epicId ? parseInt(epicId, 10) : undefined;
  const { data, isLoading, error } = useEpicTree(projectId, epicIdNum);

  if (isLoading) return <Spinner label="Loading epic tree…" />;
  if (error) return <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>;
  if (!data) return <MessageBar intent="info"><MessageBarBody>Epic not found.</MessageBarBody></MessageBar>;

  return (
    <div className={styles.page}>
      <Title2>{data.title}</Title2>
      <div className={styles.meta}>
        <StatusPill state={data.state} />
        {data.storyPoints ? <Body1>{data.storyPoints} story points</Body1> : null}
      </div>
      <Tree aria-label="Epic tree" defaultOpenItems={[`${data.id}`]}>
        {renderNode(data, styles)}
      </Tree>
    </div>
  );
}
